import { z } from 'zod';
import { deriveCurrencyStatus } from '../currency/status.js';
import { stateFipsToAbbr } from './states.js';
/**
 * The headline numbers Home and Coverage both show (mollusk-interface#171),
 * computed from ONE permits.json snapshot so the two pages never disagree.
 *
 * "Current permit data" for a city (the large-city stat): we hold credited
 * (non-thin) permits for it, the feed is current (shared currency rule: newest
 * permit within 12 months, healthy trailing volume), and it has 50+ permits per
 * 10,000 residents in the last 12 months.
 */
export const CURRENT_PERMITS_PER_10K = 50;
/** A consolidated government's county is one of its rows; an ordinary city's
 *  county only when the city is (nearly) all of it, e.g. San Francisco. */
const COEXTENSIVE_SHARE = 0.9;
/** "Nashville-Davidson metropolitan government" → "Nashville", "Austin city" → "Austin". */
export function cityDisplayName(census) {
    const consolidated = /^(.+?)[-/].*\b(metropolitan government|metro government|urban county|consolidated government|unified government)$/i.exec(census);
    if (consolidated?.[1])
        return consolidated[1];
    return census.replace(/ (city|town|village|municipality|borough|city and borough)$/i, '');
}
export function buildLargeCityRef(placePop, counties, minPopulation) {
    const countyPop = new Map(counties.map((c) => [c.county_fips, c.population]));
    const balancesOf = new Map();
    for (const [balance, parent] of Object.entries(placePop.concit_parents)) {
        balancesOf.set(parent, [...(balancesOf.get(parent) ?? []), balance]);
    }
    const partsOf = new Map();
    for (const p of placePop.place_county_parts) {
        partsOf.set(p.place_geoid, [...(partsOf.get(p.place_geoid) ?? []), p]);
    }
    const out = [];
    for (const [geoid, p] of Object.entries(placePop.places)) {
        if (geoid in placePop.concit_parents || p.population < minPopulation)
            continue;
        const balances = balancesOf.get(geoid) ?? [];
        const members = new Set([geoid, ...balances]);
        for (const g of [geoid, ...balances]) {
            for (const part of partsOf.get(g) ?? []) {
                const total = countyPop.get(part.county_fips) ?? 0;
                if (balances.length > 0 || (total > 0 && part.population >= COEXTENSIVE_SHARE * total)) {
                    members.add(part.county_fips);
                }
            }
        }
        out.push({
            geoid,
            name: cityDisplayName(p.name),
            state: stateFipsToAbbr(p.state_fips) ?? '',
            population: p.population,
            member_geoids: [...members],
        });
    }
    return out.sort((a, b) => b.population - a.population || a.name.localeCompare(b.name));
}
export const CityStat = z.object({
    geoid: z.string(),
    name: z.string(),
    state: z.string(),
    population: z.number(),
    rank: z.number().int(),
    permits_last_12mo: z.number(),
    permits_per_10k: z.number(),
    newest_permit: z.string().nullable(),
    current_permit_data: z.boolean(),
});
const OkOf = z.object({ ok: z.number().int(), total: z.number().int() });
export const CoverageStats = z.object({
    /** Date of the snapshot the numbers come from (YYYY-MM-DD). */
    as_of: z.string(),
    permits_on_record: z.number(),
    permits_last_12mo: z.number(),
    people_covered: z.number(),
    us_population: z.number(),
    share_covered: z.number(),
    large_cities: z.object({ top10: OkOf, top50: OkOf, over100k: OkOf }),
    cities: z.array(CityStat),
});
export function coverageStats(permits, cities, now) {
    const credited = permits.jurisdictions.filter((j) => !j.thin_partial);
    const byGeoid = new Map(credited.map((j) => [j.place_geoid, j]));
    const people = permits.states.reduce((n, s) => n + s.covered_population, 0);
    const us = permits.states.reduce((n, s) => n + s.total_population, 0);
    const cityStats = cities.map((c, i) => {
        const rows = c.member_geoids
            .map((g) => byGeoid.get(g))
            .filter((r) => r != null);
        const last12 = rows.reduce((n, r) => n + (r.permits_last_12mo ?? 0), 0);
        const count = rows.reduce((n, r) => n + r.permit_count, 0);
        const newest = rows.reduce((m, r) => (r.last_refresh && (!m || r.last_refresh > m) ? r.last_refresh : m), null);
        const per10k = c.population > 0 ? (last12 / c.population) * 10_000 : 0;
        const status = deriveCurrencyStatus({
            permit_count: count,
            newest_permit_date: newest,
            permits_last_12mo: last12,
            population: c.population,
        }, now);
        return {
            geoid: c.geoid,
            name: c.name,
            state: c.state,
            population: c.population,
            rank: i + 1,
            permits_last_12mo: last12,
            permits_per_10k: per10k,
            newest_permit: newest,
            current_permit_data: rows.length > 0 && status === 'current' && per10k >= CURRENT_PERMITS_PER_10K,
        };
    });
    const okOf = (list) => ({
        ok: list.filter((c) => c.current_permit_data).length,
        total: list.length,
    });
    return {
        as_of: permits.generated_at.slice(0, 10),
        permits_on_record: permits.states.reduce((n, s) => n + s.permit_count, 0),
        permits_last_12mo: credited.reduce((n, j) => n + (j.permits_last_12mo ?? 0), 0),
        people_covered: people,
        us_population: us,
        share_covered: us > 0 ? people / us : 0,
        large_cities: {
            top10: okOf(cityStats.slice(0, 10)),
            top50: okOf(cityStats.slice(0, 50)),
            over100k: okOf(cityStats.filter((c) => c.population >= 100_000)),
        },
        cities: cityStats,
    };
}
//# sourceMappingURL=headlineStats.js.map